import { useState, useEffect } from 'react';
import { Container, Card, Form, Button, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { sendRegisterOTP, verifyRegisterOTP, lookupReferralCode } from '../../api/authApi';
import { useAuth } from '../../context/AuthContext';

const COUNTRY_CODES = {
  'India': '+91',
  'United States': '+1',
  'United Kingdom': '+44',
  'United Arab Emirates': '+971',
  'Canada': '+1',
  'Australia': '+61',
  'Singapore': '+65',
  'Malaysia': '+60',
  'Saudi Arabia': '+966',
  'Qatar': '+974',
  'Bangladesh': '+880',
  'Pakistan': '+92',
  'Sri Lanka': '+94',
  'Nepal': '+977',
  'Indonesia': '+62',
  'Thailand': '+66',
  'Vietnam': '+84',
  'Philippines': '+63',
  'Hong Kong': '+852',
  'Japan': '+81',
  'South Korea': '+82',
  'Germany': '+49',
  'France': '+33',
  'Spain': '+34',
  'Italy': '+39',
};

const COUNTRIES = Object.keys(COUNTRY_CODES).sort();

const Register = () => {
  // Step 1: Email verification
  const [step, setStep] = useState(1); // 1, 2, 3
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOTP] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [canResendOTP, setCanResendOTP] = useState(false);

  // Step 2: Registration details
  const [form, setForm] = useState({ country: '', countryCode: '', mobile: '', password: '', referralCode: '' });
  const [confirmPassword, setConfirmPassword] = useState('');

  const [referralUser, setReferralUser] = useState(null); // { name } or null

  const [agreedToTnC, setAgreedToTnC] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref') || params.get('referral_code');
    if (code) setForm((prev) => ({ ...prev, referralCode: code }));
  }, []);

  // OTP timer
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (otpTimer === 0 && step === 2) setCanResendOTP(true);
  }, [otpTimer, step]);

  useEffect(() => {
    const code = form.referralCode.trim();
    if (code.length < 4) { setReferralUser(null); return; }
    const timer = setTimeout(() => {
      lookupReferralCode(code)
        .then((res) => setReferralUser(res.data.found ? res.data : null))
        .catch(() => setReferralUser(null));
    }, 400);
    return () => clearTimeout(timer);
  }, [form.referralCode]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || !username.trim() || !email.trim()) {
      return setError('Name, username, and email are required');
    }
    if (!/^[a-zA-Z][a-zA-Z0-9]{4,19}$/.test(username.trim())) {
      return setError('Username must be 5–20 characters, start with a letter, and contain only letters and numbers');
    }
    setLoading(true);
    try {
      await sendRegisterOTP({ name: name.trim(), username: username.trim(), email: email.trim() });
      setStep(2);
      setOtpTimer(30);
      setCanResendOTP(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await sendRegisterOTP({ name, username, email });
      setOtpTimer(30);
      setCanResendOTP(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    if (!otp.trim() || otp.length !== 6) {
      return setError('OTP must be 6 digits');
    }
    setLoading(true);
    try {
      setStep(3);
      // Don't clear OTP here - it's needed for final registration
    } catch (err) {
      setError(err.response?.data?.message || 'OTP verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (name === 'country' && value) {
      setForm((prev) => ({ ...prev, countryCode: COUNTRY_CODES[value] }));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (!agreedToTnC) {
      return setError('You must agree to the Terms and Conditions to register');
    }
    if (form.password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    if (form.password.length < 6) {
      return setError('Password must be at least 6 characters');
    }
    setLoading(true);
    try {
      const res = await verifyRegisterOTP({
        name,
        email,
        username,
        otp,
        country: form.country,
        countryCode: form.countryCode,
        mobile: form.mobile,
        password: form.password,
        referralCode: form.referralCode,
      });
      loginUser(res.data.token, res.data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
      <Card style={{ width: '400px' }} className="p-4 shadow">
        <h4 className="text-center mb-4">Create Account</h4>
        {error && <Alert variant="danger">{error}</Alert>}

        {step === 1 && (
          <Form onSubmit={handleSendOTP}>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>Step 1: Email Verification</p>
            <Form.Group className="mb-3">
              <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" required className="register-input" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Username <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                placeholder="e.g. johndoe"
                className="register-input"
                minLength={5}
                maxLength={20}
                required
              />
              <Form.Text className="text-muted">5–20 characters, must start with a letter, letters and numbers only</Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Email <span className="text-danger">*</span></Form.Label>
              <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required className="register-input" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Referral Code <span className="text-danger">*</span></Form.Label>
              <Form.Control type="text" name="referralCode" value={form.referralCode} onChange={handleChange} placeholder="9-digit code" maxLength={9} required className="register-input" />
              {form.referralCode.trim().length >= 4 && (
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  {referralUser
                    ? <span style={{ color: '#66ff99' }}>✓ {referralUser.name}</span>
                    : <span style={{ color: '#ff6666' }}>No user found</span>
                  }
                </div>
              )}
            </Form.Group>
            <Button type="submit" className="w-100" disabled={loading}>
              {loading ? 'Sending OTP...' : 'Send OTP to Email'}
            </Button>
          </Form>
        )}

        {step === 2 && (
          <Form onSubmit={handleVerifyOTP}>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>Step 2: Verify OTP</p>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>A 6-digit OTP was sent to <strong>{email}</strong></p>
            <Form.Group className="mb-3">
              <Form.Label>Enter OTP <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={otp}
                onChange={(e) => setOTP(e.target.value)}
                placeholder="000000"
                maxLength="6"
                pattern="[0-9]{6}"
                required
                className="register-input"
              />
            </Form.Group>
            {otpTimer > 0 && <p style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>Resend available in {otpTimer}s</p>}
            <Button type="submit" className="w-100 mb-2" disabled={loading || otp.length !== 6}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </Button>
            <Button
              type="button"
              variant="outline-secondary"
              className="w-100"
              onClick={handleResendOTP}
              disabled={!canResendOTP || loading}
            >
              Resend OTP
            </Button>
            <Button
              type="button"
              variant="link"
              className="w-100 mt-2 text-decoration-none"
              onClick={() => { setStep(1); setOtpTimer(0); setCanResendOTP(false); setOTP(''); }}
            >
              ← Change Email
            </Button>
          </Form>
        )}

        {step === 3 && (
          <Form onSubmit={handleRegister}>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>Step 3: Complete Registration</p>
            <Form.Group className="mb-3">
              <Form.Label>Country <span className="text-danger">*</span></Form.Label>
              <Form.Select name="country" value={form.country} onChange={handleChange} required>
                <option value="">Select country</option>
                {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Form.Group>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 10, marginBottom: 12 }}>
              <Form.Group>
                <Form.Label style={{ fontSize: 12 }}>Code <span className="text-danger">*</span></Form.Label>
                <Form.Control type="text" value={form.countryCode} readOnly style={{ background: '#e9ecef', cursor: 'not-allowed' }} />
              </Form.Group>
              <Form.Group>
                <Form.Label style={{ fontSize: 12 }}>Mobile <span className="text-danger">*</span></Form.Label>
                <Form.Control type="tel" name="mobile" value={form.mobile} onChange={handleChange} pattern="[0-9]{10,15}" placeholder="9876543210" required className="register-input" />
              </Form.Group>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Password <span className="text-danger">*</span></Form.Label>
              <Form.Control type="password" name="password" value={form.password} onChange={handleChange} minLength={6} required className="register-input" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Re-enter Password <span className="text-danger">*</span></Form.Label>
              <Form.Control type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="register-input" />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="tnc-checkbox"
                checked={agreedToTnC}
                onChange={(e) => setAgreedToTnC(e.target.checked)}
                label={
                  <span style={{ fontSize: 13 }}>
                    I agree to OneCircle's{' '}
                    <span style={{ color: 'var(--bs-primary)', textDecoration: 'underline' }}>
                      Terms and Conditions
                    </span>
                  </span>
                }
              />
            </Form.Group>
            <Button type="submit" className="w-100" disabled={loading}>
              {loading ? 'Creating account...' : 'Register'}
            </Button>
          </Form>
        )}

        <div className="text-center mt-3">
          <small>Already have an account? <Link to="/login">Sign in</Link></small>
        </div>
      </Card>

    </Container>
  );
};

export default Register;
